(() => {
  const roots = document.querySelectorAll('[data-kl-product-addons]');
  if (!roots.length) return;

  const routes = (window.KROWN && KROWN.settings && KROWN.settings.routes) || {};
  const addUrl = `${routes.cart_add_url || '/cart/add'}.js`;
  const changeUrl = `${routes.cart_change_url || '/cart/change'}.js`;
  const SYNC_DELAY = 220;

  async function getCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Cart fetch failed');
    return res.json();
  }

  async function postJson(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.status === 422) {
      throw new Error(data.description || data.message || 'Cart update failed');
    }
    return data;
  }

  function findItem(cart, variantId) {
    const id = Number(variantId);
    return (cart.items || []).find((item) => Number(item.variant_id) === id);
  }

  function setCardUI(card, qty) {
    const addBtn = card.querySelector('[data-kl-addon-add]');
    const stepper = card.querySelector('[data-kl-addon-stepper]');
    const qtyEl = card.querySelector('[data-kl-addon-qty]');
    if (!addBtn || !stepper || !qtyEl) return;

    // Don't overwrite while the shopper is typing
    if (document.activeElement === qtyEl) return;

    const hasQty = Number(qty) > 0;
    addBtn.hidden = hasQty;
    stepper.hidden = !hasQty;
    qtyEl.value = String(hasQty ? qty : 1);
    card.dataset.qty = hasQty ? String(qty) : '0';
    document.dispatchEvent(new CustomEvent('kl:addons-changed', { bubbles: true }));
  }

  function getCardQty(card) {
    const qtyEl = card.querySelector('[data-kl-addon-qty]');
    if (qtyEl && document.activeElement === qtyEl) {
      const typed = parseInt(qtyEl.value, 10);
      if (!Number.isNaN(typed)) return Math.max(0, Math.min(999, typed));
    }
    const stored = Number(card.dataset.qty);
    if (!Number.isNaN(stored)) return stored;
    const stepper = card.querySelector('[data-kl-addon-stepper]');
    if (stepper && !stepper.hidden && qtyEl) {
      const val = parseInt(qtyEl.value, 10);
      return Number.isNaN(val) ? 0 : val;
    }
    return 0;
  }

  async function refreshThemeCart(openDrawer) {
    if (typeof window.refreshCart === 'function' && document.getElementById('AjaxCartForm')) {
      if (openDrawer) {
        window.refreshCart();
        return;
      }
    }

    try {
      const res = await fetch('?section_id=helper-cart');
      const text = await res.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const formSource = doc.getElementById('AjaxCartForm');
      const subtotalSource = doc.getElementById('AjaxCartSubtotal');
      const cartItems = document.getElementById('AjaxCartForm');
      if (formSource && cartItems) {
        cartItems.innerHTML = formSource.innerHTML;
        if (typeof cartItems.ajaxifyCartItems === 'function') cartItems.ajaxifyCartItems();
        document.querySelectorAll('[data-header-cart-count]').forEach((el) => {
          const count = cartItems.querySelector('[data-cart-count]');
          if (count) el.textContent = count.textContent;
        });
      }
      const subtotalTarget = document.getElementById('AjaxCartSubtotal');
      if (subtotalSource && subtotalTarget) {
        subtotalTarget.innerHTML = subtotalSource.innerHTML;
      }
    } catch (e) {
      console.warn('[KL add-ons] theme cart refresh failed', e);
    }
  }

  function initRoot(root) {
    const viewport = root.querySelector('[data-kl-addon-viewport]');
    const track = root.querySelector('[data-kl-addon-track]');
    const cards = Array.from(root.querySelectorAll('[data-kl-addon-card]'));
    if (!track || !cards.length) return;

    const prev = root.querySelector('[data-kl-addon-prev]');
    const next = root.querySelector('[data-kl-addon-next]');
    const pendingTimers = new WeakMap();
    const pendingPromises = new WeakMap();
    let suppressExternalSync = 0;

    function cardStep() {
      const card = cards[0];
      if (!card || !viewport) return 120;
      const styles = window.getComputedStyle(track);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;
      return card.getBoundingClientRect().width + gap;
    }

    function updateNav() {
      if (!viewport || (!prev && !next)) return;
      const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth - 1);
      if (prev) prev.disabled = viewport.scrollLeft <= 1;
      if (next) next.disabled = viewport.scrollLeft >= maxScroll;
    }

    function scrollByCard(direction) {
      if (!viewport) return;
      viewport.scrollBy({ left: direction * cardStep(), behavior: 'smooth' });
    }

    if (prev) prev.addEventListener('click', () => scrollByCard(-1));
    if (next) next.addEventListener('click', () => scrollByCard(1));
    if (viewport) {
      viewport.addEventListener('scroll', updateNav, { passive: true });
      window.addEventListener('resize', updateNav);
      updateNav();
    }

    async function syncAll() {
      if (suppressExternalSync > 0) return;
      try {
        const cart = await getCart();
        cards.forEach((card) => {
          if (pendingTimers.has(card) || pendingPromises.has(card)) return;
          const qtyEl = card.querySelector('[data-kl-addon-qty]');
          if (qtyEl && document.activeElement === qtyEl) return;
          const item = findItem(cart, card.dataset.variantId);
          setCardUI(card, item ? item.quantity : 0);
          if (item && item.key) card.dataset.itemKey = item.key;
          else delete card.dataset.itemKey;
        });
      } catch (e) {
        console.warn('[KL add-ons] sync failed', e);
      }
    }

    async function commitCard(card, openDrawer) {
      const variantId = Number(card.dataset.variantId);
      if (!variantId) return;

      const desiredQty = getCardQty(card);
      const previousQty = Number(card.dataset.committedQty || 0);

      try {
        suppressExternalSync += 1;

        if (desiredQty <= 0) {
          if (previousQty > 0) {
            const key = card.dataset.itemKey;
            if (key) {
              await postJson(changeUrl, { id: key, quantity: 0 });
            } else {
              const cart = await getCart();
              const item = findItem(cart, variantId);
              if (item) await postJson(changeUrl, { id: item.key, quantity: 0 });
            }
            delete card.dataset.itemKey;
          }
        } else if (previousQty <= 0 || !card.dataset.itemKey) {
          const added = await postJson(addUrl, {
            id: variantId,
            quantity: desiredQty
          });
          const key =
            (added && added.key) ||
            (added && added.items && added.items[0] && added.items[0].key);
          if (key) card.dataset.itemKey = key;
          else {
            const cart = await getCart();
            const item = findItem(cart, variantId);
            if (item && item.key) card.dataset.itemKey = item.key;
          }
        } else if (desiredQty !== previousQty) {
          await postJson(changeUrl, {
            id: card.dataset.itemKey,
            quantity: desiredQty
          });
        }

        card.dataset.committedQty = String(desiredQty);
        await refreshThemeCart(Boolean(openDrawer && desiredQty > 0));
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
      } catch (e) {
        console.warn('[KL add-ons] action failed', e);
        try {
          const cart = await getCart();
          const item = findItem(cart, variantId);
          setCardUI(card, item ? item.quantity : 0);
          card.dataset.committedQty = item ? String(item.quantity) : '0';
          if (item && item.key) card.dataset.itemKey = item.key;
          else delete card.dataset.itemKey;
        } catch (_) {
          setCardUI(card, previousQty);
          card.dataset.committedQty = String(previousQty);
        }
        alert(e.message || 'Could not update cart. Please try again.');
      } finally {
        suppressExternalSync = Math.max(0, suppressExternalSync - 1);
        pendingPromises.delete(card);
      }
    }

    function queueCommit(card, openDrawer) {
      const existing = pendingTimers.get(card);
      if (existing) clearTimeout(existing);

      const shouldOpen = openDrawer || card.dataset.openDrawer === 'true';
      if (openDrawer) card.dataset.openDrawer = 'true';

      const timer = setTimeout(() => {
        pendingTimers.delete(card);
        const open = card.dataset.openDrawer === 'true';
        delete card.dataset.openDrawer;
        const promise = commitCard(card, open || shouldOpen);
        pendingPromises.set(card, promise);
      }, SYNC_DELAY);

      pendingTimers.set(card, timer);
    }

    function updateCard(card, action) {
      const variantId = Number(card.dataset.variantId);
      if (!variantId) return;

      const qtyEl = card.querySelector('[data-kl-addon-qty]');
      if (qtyEl && document.activeElement === qtyEl) qtyEl.blur();

      if (card.dataset.committedQty === undefined) {
        card.dataset.committedQty = String(getCardQty(card));
      }

      const currentQty = getCardQty(card);
      let nextQty = currentQty;
      let openDrawer = false;

      if (action === 'add') {
        nextQty = Math.max(1, currentQty || 1);
        openDrawer = true;
      } else if (action === 'plus') {
        nextQty = Math.min(999, currentQty + 1);
      } else if (action === 'minus') {
        nextQty = Math.max(0, currentQty - 1);
      }

      // Instant UI — same feel as the main product quantity selector
      setCardUI(card, nextQty);
      queueCommit(card, openDrawer);
    }

    function applyTypedQty(card) {
      const qtyEl = card.querySelector('[data-kl-addon-qty]');
      if (!qtyEl) return;

      if (card.dataset.committedQty === undefined) {
        card.dataset.committedQty = String(getCardQty(card));
      }

      let nextQty = parseInt(qtyEl.value, 10);
      if (Number.isNaN(nextQty) || nextQty < 0) nextQty = 0;
      nextQty = Math.min(999, nextQty);

      setCardUI(card, nextQty);
      queueCommit(card, false);
    }

    root.addEventListener('click', (event) => {
      const card = event.target.closest('[data-kl-addon-card]');
      if (!card) return;
      if (event.target.closest('[data-kl-addon-qty]')) return;
      if (event.target.closest('[data-kl-addon-add]')) updateCard(card, 'add');
      else if (event.target.closest('[data-kl-addon-plus]')) updateCard(card, 'plus');
      else if (event.target.closest('[data-kl-addon-minus]')) updateCard(card, 'minus');
    });

    root.addEventListener('focusin', (event) => {
      const qtyEl = event.target.closest('[data-kl-addon-qty]');
      if (!qtyEl) return;
      // Select so shoppers can type a new number immediately
      requestAnimationFrame(() => qtyEl.select());
    });

    root.addEventListener('keydown', (event) => {
      const qtyEl = event.target.closest('[data-kl-addon-qty]');
      if (!qtyEl) return;
      const card = qtyEl.closest('[data-kl-addon-card]');
      if (!card) return;

      if (event.key === 'Enter') {
        event.preventDefault();
        qtyEl.blur();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        const restore = Number(card.dataset.qty);
        qtyEl.value = String(!Number.isNaN(restore) && restore > 0 ? restore : 1);
        qtyEl.blur();
      }
    });

    root.addEventListener('focusout', (event) => {
      const qtyEl = event.target.closest('[data-kl-addon-qty]');
      if (!qtyEl) return;
      const card = qtyEl.closest('[data-kl-addon-card]');
      if (!card) return;
      applyTypedQty(card);
    });

    syncAll();
    document.addEventListener('cart:updated', syncAll);
    document.addEventListener('cart:refresh', syncAll);
  }

  roots.forEach(initRoot);
})();
